import { type ReactNode, useEffect } from "react"

import { create } from "zustand"

interface HeaderSlotState {
  rightSlot: ReactNode
  setRightSlot: (node: ReactNode) => void
}

const useHeaderSlotStore = create<HeaderSlotState>((set) => ({
  rightSlot: null,
  setRightSlot(node) {
    set({ rightSlot: node })
  },
}))

/**
 * Read the currently-mounted right-slot content. Used by `Header` to render
 * page-injected items alongside the shared header chrome.
 */
export function useHeaderRightSlot(): ReactNode {
  return useHeaderSlotStore((s) => s.rightSlot)
}

/**
 * Inject children into the app Header's right-aligned slot from anywhere in
 * the page tree. The wrapper itself renders nothing — content shows up via
 * the matching `<HeaderRightSlotOutlet />` in the header.
 *
 * Mount one slot at a time. If multiple pages mount competing slots the last
 * mount wins; on unmount the slot resets to whatever the previous mount set
 * (in practice we only have one page rendered at a time).
 */
export function HeaderRightSlot({ children }: { children: ReactNode }) {
  const setRightSlot = useHeaderSlotStore((s) => s.setRightSlot)
  useEffect(() => {
    setRightSlot(children)
    return () => setRightSlot(null)
  })
  return null
}
